import { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';

import { useActions } from '../../../../../hooks/useActions';
import { useModalDetails } from '../../../../../hooks/useModalDetails';
import { useProcessing } from '../../../../../hooks/useProcessing';
import { useCancellationToken } from '../../../../../hooks/useTokenCancellation';
import { IUpdateDOCMembershipOptionsModel } from '../../../../../models/interfaces/Membership/DutyOfCare/UpdateDOCMembershipOptionsModel';
import { updateModalState } from '../../../../../store/redux-slices/modals';
import { UPDATE_MEMBERSHIP_OPTIONS_PROCESSING } from '../../../../../store/redux-slices/processes/constants';
import { updateDOCMembershipOptions } from '../../../../../store/saga-slices/membership/dutyOfCare';
import { IUpdateDOCMembershipOptionsPayload } from '../../../../../store/saga-slices/membership/dutyOfCare/models';
import { EDIT_DOC_MEMBERSHIP_OPTIONS_MODAL } from './editDOCMembershipOptionsModal.const';
import { EditDOCMembershipOptionsModalDetails } from './editDOCMembershipOptionsModal.models';

type UseEditDOCMembershipOptionsModalProps = {
  onMembershipOptionsUpdated?: () => void;
  id: string;
};

export const useEditDOCMembershipOptionsModal = ({
  id,
  onMembershipOptionsUpdated,
}: UseEditDOCMembershipOptionsModalProps) => {
  const alert = useAlert();
  const { t } = useTranslation();
  const UpdateMembershipOptions = useActions(updateDOCMembershipOptions);
  const UpdateModalState = useActions(updateModalState);
  const cancellationToken = useCancellationToken();

  const isProcessing = useProcessing(UPDATE_MEMBERSHIP_OPTIONS_PROCESSING);

  const {
    membershipOptions,
    membershipId,
  }: EditDOCMembershipOptionsModalDetails = useModalDetails(
    EDIT_DOC_MEMBERSHIP_OPTIONS_MODAL + id,
  );

  const updateMembershipOptionsHandler = useCallback(
    (values: IUpdateDOCMembershipOptionsModel) => {
      const payload: IUpdateDOCMembershipOptionsPayload = {
        membershipId,
        options: values,
        cancellationToken,
        success: () => {
          UpdateModalState(EDIT_DOC_MEMBERSHIP_OPTIONS_MODAL + id);
          onMembershipOptionsUpdated?.();
          alert.success(t('alerts.membership-options-update-success'));
        },
        error: () => alert.error(t('alerts.membership-options-update-fail')),
      };

      UpdateMembershipOptions(payload);
    },
    [
      UpdateMembershipOptions,
      UpdateModalState,
      alert,
      cancellationToken,
      id,
      membershipId,
      onMembershipOptionsUpdated,
      t,
    ],
  );

  const onCloseHandler = () => {
    UpdateModalState(EDIT_DOC_MEMBERSHIP_OPTIONS_MODAL + id);
  };
  return {
    updateMembershipOptionsHandler,
    membershipOptions,
    isProcessing,
    onCloseHandler,
  };
};
